import { ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useOrganization } from '../../../context/OrganizationContext';
import { billingService } from '../../../services/billingService';
import { BillingCycle, BillingInfo, PlanType } from '../../../types/billing';
import { SubscriptionChangePreview } from '../SubscriptionChangePreview';

interface SubscriptionManagerV2Props {
  billingData: BillingInfo;
  onUpdate: () => void;
}

interface PricingData {
  plan_type: string;
  monthly_base_price: number | null;
  annual_base_price: number | null;
  monthly_editor_price: number | null;
  annual_editor_price: number | null;
  currency: string;
}

export const SubscriptionManagerV2: React.FC<SubscriptionManagerV2Props> = ({
  billingData,
  onUpdate,
}) => {
  const { selectedOrg } = useOrganization();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(
    billingData.plan_type as PlanType,
  );
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingCycle>(
      (billingData.billing_cycle || 'monthly') as BillingCycle,
    );
  const [selectedEditors, setSelectedEditors] = useState(
    billingData.current_editors || 1,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PricingData[]>([]);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoadingPricing(true);
      const pricingData = await billingService.getPricing();
      setPricing(pricingData);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  const getPlansWithPricing = () => {
    const starterPricing = pricing.find((p) => p.plan_type === 'starter');
    const proPricing = pricing.find((p) => p.plan_type === 'professional');

    return [
      {
        type: 'starter' as PlanType,
        name: 'Starter',
        monthlyPrice: starterPricing?.monthly_base_price
          ? starterPricing.monthly_base_price / 100
          : null,
        annualPrice: starterPricing?.annual_base_price
          ? starterPricing.annual_base_price / 100
          : null,
        features: [
          'Up to 5 editors',
          '3 value propositions',
          '3 market segments',
          'AI research assistant',
          'Basic analytics',
        ],
        recommended: billingData.plan_type === 'free',
      },
      {
        type: 'professional' as PlanType,
        name: 'Professional',
        monthlyPrice: proPricing?.monthly_base_price
          ? proPricing.monthly_base_price / 100
          : null,
        annualPrice: proPricing?.annual_base_price
          ? proPricing.annual_base_price / 100
          : null,
        features: [
          'Unlimited editors',
          'Unlimited propositions',
          'Unlimited segments',
          'Advanced AI research',
          'Priority support',
        ],
        recommended: billingData.current_editors > 5,
      },
    ];
  };

  const plans = getPlansWithPricing();

  const hasChanges = () => {
    return (
      selectedPlan !== billingData.plan_type ||
      selectedBillingCycle !== billingData.billing_cycle ||
      selectedEditors !== billingData.current_editors
    );
  };

  const getChangeDescription = () => {
    const changes = [];

    if (selectedPlan !== billingData.plan_type) {
      changes.push(`${billingData.plan_type} → ${selectedPlan}`);
    }

    if (selectedBillingCycle !== billingData.billing_cycle) {
      changes.push(`${billingData.billing_cycle} → ${selectedBillingCycle}`);
    }

    if (selectedEditors !== billingData.current_editors) {
      changes.push(
        `${billingData.current_editors} → ${selectedEditors} editors`,
      );
    }

    return changes.join(', ');
  };

  const handleApplyChanges = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Simplified logic - let the backend handle the complexity
      if (selectedPlan === 'free') {
        // Canceling to free
        await billingService.cancelSubscription(
          billingData.organization_id,
          true,
        );
      } else if (billingData.plan_type === 'free') {
        // If on free plan and not trialing, require explicit Start Trial or explicit Checkout
        // Here we open a preview to show the expected cost before redirecting to payment
        setShowPreview(true);
        setIsLoading(false);
        return;
      } else {
        // Modification - let backend determine if immediate or scheduled
        await billingService.upgradeSubscription(
          billingData.organization_id,
          selectedPlan,
          selectedBillingCycle,
          selectedEditors,
        );
      }

      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmFromPreview = async () => {
    try {
      setIsLoading(true);
      const session = await billingService.createSubscription(
        billingData.organization_id,
        selectedPlan,
        selectedBillingCycle,
        selectedEditors,
      );
      window.location.href = session.checkout_url;
    } catch (err: any) {
      setError(
        err.response?.data?.detail || 'Failed to start checkout process',
      );
    } finally {
      setIsLoading(false);
      setShowPreview(false);
    }
  };

  // For free plan users, show upgrade-focused UI
  if (billingData.plan_type === 'free') {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Choose Your Plan
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          {plans.map((plan) => (
            <div
              key={plan.type}
              className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all ${
                selectedPlan === plan.type
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              } ${plan.recommended ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setSelectedPlan(plan.type)}
            >
              {plan.recommended && (
                <span className="absolute -top-3 left-4 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  Recommended
                </span>
              )}
              <div className="flex justify-between items-start mb-3">
                <h4 className="text-lg font-semibold text-gray-900">
                  {plan.name}
                </h4>
                {selectedPlan === plan.type && (
                  <CheckIcon className="h-5 w-5 text-blue-500" />
                )}
              </div>
              <div className="mb-3">
                {loadingPricing ? (
                  <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
                ) : (
                  <>
                    <span className="text-2xl font-bold text-gray-900">
                      {(() => {
                        const pricingRow = pricing.find(
                          (p) => p.plan_type === plan.type,
                        );
                        const currencyCode =
                          pricingRow?.currency?.toUpperCase() || 'GBP';
                        const value =
                          selectedBillingCycle === 'monthly'
                            ? plan.monthlyPrice
                            : plan.annualPrice;
                        if (!value) return 'Contact us';
                        return new Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency: currencyCode,
                        }).format(value);
                      })()}
                    </span>
                    {(selectedBillingCycle === 'monthly'
                      ? plan.monthlyPrice
                      : plan.annualPrice) && (
                      <span className="text-gray-500 text-sm ml-1">
                        /{selectedBillingCycle === 'monthly' ? 'month' : 'year'}
                      </span>
                    )}
                  </>
                )}
              </div>
              <ul className="space-y-2">
                {plan.features.map((feature, idx) => (
                  <li
                    key={idx}
                    className="flex items-start text-sm text-gray-600"
                  >
                    <CheckIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Billing Cycle Toggle */}
        <div className="mt-6 flex justify-center">
          <div className="bg-gray-100 p-1 rounded-lg inline-flex">
            <button
              onClick={() => setSelectedBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                selectedBillingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedBillingCycle('annual')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                selectedBillingCycle === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual
              <span className="ml-1 text-xs text-green-600 font-normal">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <button
            onClick={async () => {
              try {
                setIsLoading(true);
                await billingService.startTrial(billingData.organization_id);
                onUpdate();
              } catch (err: any) {
                setError(
                  err.response?.data?.detail || 'Failed to start free trial',
                );
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading || billingData.is_trial_active}
            className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Starting...' : 'Start Free Trial'}
          </button>
          <button
            onClick={handleApplyChanges}
            disabled={!selectedPlan || selectedPlan === 'free' || isLoading}
            className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processing...' : 'Go to Checkout'}
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </button>
        </div>

        {/* Preview dialog for pricing before checkout */}
        <SubscriptionChangePreview
          open={showPreview}
          onClose={() => setShowPreview(false)}
          onConfirm={handleConfirmFromPreview}
          organizationId={billingData.organization_id}
          planType={selectedPlan}
          billingCycle={selectedBillingCycle}
          editorCount={selectedEditors}
          loading={isLoading}
          currencyCode={pricing
            .find((p) => p.plan_type === selectedPlan)
            ?.currency?.toUpperCase()}
        />
      </div>
    );
  }

  // For existing subscribers, show modification UI
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Modify Subscription
      </h3>

      {/* Quick Actions */}
      <div className="grid gap-3 mb-6">
        <button
          onClick={() => setSelectedEditors(selectedEditors + 1)}
          className="flex justify-between items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <span className="text-sm font-medium text-gray-900">
            Add Editor Seat
          </span>
          <span className="text-sm text-gray-500">
            {(() => {
              const currentPlanPricing = pricing.find(
                (p) => p.plan_type === billingData.plan_type,
              );
              if (!currentPlanPricing) return 'See pricing';

              const editorPrice =
                billingData.billing_cycle === 'annual'
                  ? currentPlanPricing.annual_editor_price
                  : currentPlanPricing.monthly_editor_price;

              if (!editorPrice) return 'Contact us';
              const currencyCode =
                currentPlanPricing.currency?.toUpperCase() || 'GBP';
              const formatted = new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: currencyCode,
              }).format(editorPrice / 100);
              return `+${formatted}/${billingData.billing_cycle === 'annual' ? 'year' : 'month'}`;
            })()}
          </span>
        </button>

        {billingData.plan_type === 'starter' && (
          <button
            onClick={() => setSelectedPlan('professional')}
            className="flex justify-between items-center p-3 border border-blue-200 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            <span className="text-sm font-medium text-blue-900">
              Upgrade to Professional
            </span>
            <ArrowRightIcon className="h-4 w-4 text-blue-600" />
          </button>
        )}

        {billingData.billing_cycle === 'monthly' && (
          <button
            onClick={() => setSelectedBillingCycle('annual')}
            className="flex justify-between items-center p-3 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100"
          >
            <span className="text-sm font-medium text-green-900">
              Switch to Annual Billing
            </span>
            <span className="text-sm text-green-600">Save 17%</span>
          </button>
        )}
      </div>

      {/* Show changes preview */}
      {hasChanges() && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-900">
            <span className="font-medium">Pending Changes:</span>{' '}
            {getChangeDescription()}
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {hasChanges() && (
        <div className="flex gap-3">
          <button
            onClick={() => {
              setSelectedPlan(billingData.plan_type as PlanType);
              setSelectedBillingCycle(
                (billingData.billing_cycle || 'monthly') as BillingCycle,
              );
              setSelectedEditors(billingData.current_editors);
            }}
            className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyChanges}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Processing...' : 'Apply Changes'}
          </button>
        </div>
      )}
    </div>
  );
};
